/**
 * Shared web transition engine. Turns a stable transition id + easing +
 * duration into concrete CSS start/end styles for a two-layer
 * (outgoing + incoming) crossfade. Used by BOTH the real web player
 * (Player/index.tsx) and the editor preview (PreviewDialog) so what an
 * operator previews is exactly what a device renders.
 *
 * Model: the incoming layer paints on top of the outgoing layer. Each
 * layer animates from a `*From` style to a `*To` style over `durationMs`.
 * Effects where the outgoing layer "stays" simply keep identity styles
 * and are unmounted when the transition ends.
 */
import type { CSSProperties } from "react";
import { clampDurationMs, DEFAULT_EASING, isVideoSafe } from "./transitions";

export interface TransitionPlan {
  durationMs: number;
  timing: string;
  instant: boolean;
  perspective: boolean;
  incomingFrom: CSSProperties;
  incomingTo: CSSProperties;
  outgoingFrom: CSSProperties;
  outgoingTo: CSSProperties;
}

const EASING_CSS: Record<string, string> = {
  linear: "linear",
  ease: "ease",
  ease_in: "ease-in",
  ease_out: "ease-out",
  ease_in_out: "ease-in-out",
};

const identity: CSSProperties = { opacity: 1, transform: "none", filter: "none", clipPath: "none" };

function cut(durationMs: number, timing: string): TransitionPlan {
  return {
    durationMs: 0, timing, instant: true, perspective: false,
    incomingFrom: identity, incomingTo: identity,
    outgoingFrom: { opacity: 0 }, outgoingTo: { opacity: 0 },
  };
}

/**
 * Build the animation plan for a transition. `videoAware` downgrades
 * clip/rotate/blur effects to a crossfade when the incoming or outgoing
 * scene is a video (the <video> element fights clip-path / 3d transforms).
 */
export function buildTransitionPlan(
  id: string | null | undefined,
  easingId: string | null | undefined,
  durationMs: number | null | undefined,
  opts: { involvesVideo?: boolean } = {},
): TransitionPlan {
  const timing = EASING_CSS[easingId || DEFAULT_EASING] || "ease-in-out";
  const dur = clampDurationMs(durationMs);

  let effect = id || "cut";
  if (effect === "none" || effect === "cut") return cut(dur, timing);

  // Video-safety downgrade: unsafe effect over a video → crossfade.
  if (opts.involvesVideo && !isVideoSafe(effect)) effect = "crossfade";

  const base = (extra: Partial<TransitionPlan>): TransitionPlan => ({
    durationMs: dur, timing, instant: false, perspective: false,
    incomingFrom: identity, incomingTo: identity,
    outgoingFrom: identity, outgoingTo: identity,
    ...extra,
  });

  switch (effect) {
    case "fade":
      return base({ incomingFrom: { opacity: 0 }, incomingTo: { opacity: 1 } });
    case "crossfade":
    case "dissolve":
      return base({
        incomingFrom: { opacity: 0 }, incomingTo: { opacity: 1 },
        outgoingFrom: { opacity: 1 }, outgoingTo: { opacity: 0 },
      });

    // Slides + pushes — both layers translate together.
    case "slide_left":
    case "push_left":
      return base({
        incomingFrom: { transform: "translateX(100%)" }, incomingTo: { transform: "translateX(0)" },
        outgoingFrom: { transform: "translateX(0)" }, outgoingTo: { transform: "translateX(-100%)" },
      });
    case "slide_right":
    case "push_right":
      return base({
        incomingFrom: { transform: "translateX(-100%)" }, incomingTo: { transform: "translateX(0)" },
        outgoingFrom: { transform: "translateX(0)" }, outgoingTo: { transform: "translateX(100%)" },
      });
    case "slide_up":
    case "push_up":
      return base({
        incomingFrom: { transform: "translateY(100%)" }, incomingTo: { transform: "translateY(0)" },
        outgoingFrom: { transform: "translateY(0)" }, outgoingTo: { transform: "translateY(-100%)" },
      });
    case "slide_down":
    case "push_down":
      return base({
        incomingFrom: { transform: "translateY(-100%)" }, incomingTo: { transform: "translateY(0)" },
        outgoingFrom: { transform: "translateY(0)" }, outgoingTo: { transform: "translateY(100%)" },
      });

    // Wipes / reveal — incoming clipped, outgoing stays beneath.
    case "wipe_left":
      return base({ incomingFrom: { clipPath: "inset(0 0 0 100%)" }, incomingTo: { clipPath: "inset(0 0 0 0)" } });
    case "wipe_right":
    case "reveal":
      return base({ incomingFrom: { clipPath: "inset(0 100% 0 0)" }, incomingTo: { clipPath: "inset(0 0 0 0)" } });
    case "wipe_up":
      return base({ incomingFrom: { clipPath: "inset(100% 0 0 0)" }, incomingTo: { clipPath: "inset(0 0 0 0)" } });
    case "wipe_down":
      return base({ incomingFrom: { clipPath: "inset(0 0 100% 0)" }, incomingTo: { clipPath: "inset(0 0 0 0)" } });

    // Zoom / scale
    case "zoom_in":
      return base({ incomingFrom: { transform: "scale(0.6)", opacity: 0 }, incomingTo: { transform: "scale(1)", opacity: 1 } });
    case "zoom_out":
      return base({ incomingFrom: { transform: "scale(1.4)", opacity: 0 }, incomingTo: { transform: "scale(1)", opacity: 1 } });
    case "zoom_fade":
    case "ken_burns":
      return base({
        incomingFrom: { transform: "scale(1.1)", opacity: 0 }, incomingTo: { transform: "scale(1)", opacity: 1 },
        outgoingFrom: { opacity: 1 }, outgoingTo: { opacity: 0 },
      });
    case "scale_fade":
      return base({
        incomingFrom: { transform: "scale(0.9)", opacity: 0 }, incomingTo: { transform: "scale(1)", opacity: 1 },
        outgoingFrom: { opacity: 1 }, outgoingTo: { opacity: 0 },
      });
    case "blur_fade":
      return base({
        incomingFrom: { filter: "blur(14px)", opacity: 0 }, incomingTo: { filter: "blur(0)", opacity: 1 },
        outgoingFrom: { opacity: 1 }, outgoingTo: { opacity: 0 },
      });

    // 3D flips / rotate (need perspective on the stage)
    case "flip_horizontal":
      return base({
        perspective: true,
        incomingFrom: { transform: "rotateY(90deg)", opacity: 0 }, incomingTo: { transform: "rotateY(0deg)", opacity: 1 },
        outgoingFrom: { transform: "rotateY(0deg)" }, outgoingTo: { transform: "rotateY(-90deg)", opacity: 0 },
      });
    case "flip_vertical":
      return base({
        perspective: true,
        incomingFrom: { transform: "rotateX(90deg)", opacity: 0 }, incomingTo: { transform: "rotateX(0deg)", opacity: 1 },
        outgoingFrom: { transform: "rotateX(0deg)" }, outgoingTo: { transform: "rotateX(-90deg)", opacity: 0 },
      });
    case "rotate_fade":
      return base({
        incomingFrom: { transform: "rotate(8deg) scale(0.92)", opacity: 0 }, incomingTo: { transform: "rotate(0) scale(1)", opacity: 1 },
        outgoingFrom: { opacity: 1 }, outgoingTo: { opacity: 0 },
      });

    // Pans — gentle directional drift + fade.
    case "pan_left":
      return base({
        incomingFrom: { transform: "translateX(30%)", opacity: 0 }, incomingTo: { transform: "translateX(0)", opacity: 1 },
        outgoingFrom: { transform: "translateX(0)", opacity: 1 }, outgoingTo: { transform: "translateX(-30%)", opacity: 0 },
      });
    case "pan_right":
      return base({
        incomingFrom: { transform: "translateX(-30%)", opacity: 0 }, incomingTo: { transform: "translateX(0)", opacity: 1 },
        outgoingFrom: { transform: "translateX(0)", opacity: 1 }, outgoingTo: { transform: "translateX(30%)", opacity: 0 },
      });
    case "pan_up":
      return base({
        incomingFrom: { transform: "translateY(30%)", opacity: 0 }, incomingTo: { transform: "translateY(0)", opacity: 1 },
        outgoingFrom: { transform: "translateY(0)", opacity: 1 }, outgoingTo: { transform: "translateY(-30%)", opacity: 0 },
      });
    case "pan_down":
      return base({
        incomingFrom: { transform: "translateY(-30%)", opacity: 0 }, incomingTo: { transform: "translateY(0)", opacity: 1 },
        outgoingFrom: { transform: "translateY(0)", opacity: 1 }, outgoingTo: { transform: "translateY(30%)", opacity: 0 },
      });

    default:
      // Unknown / legacy id → safe fade fallback (never crash).
      return base({ incomingFrom: { opacity: 0 }, incomingTo: { opacity: 1 } });
  }
}

/** The `transition` shorthand string that drives every layer property. */
export function transitionCss(plan: TransitionPlan): string {
  const d = plan.durationMs;
  const t = plan.timing;
  return `transform ${d}ms ${t}, opacity ${d}ms ${t}, filter ${d}ms ${t}, clip-path ${d}ms ${t}`;
}
